<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import {
  useFunctionGetter,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import WootReports from './components/WootReports.vue';
import AgentSessionsTab from './components/AgentSessionsTab.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const agent = useFunctionGetter('agents/getAgentById', route.params.id);
const inboxes = useMapGetter('inboxes/getInboxes');

// Tab state
const activeTab = ref('metrics');

const tabs = computed(() => [
  {
    id: 'metrics',
    label: t('AGENT_REPORTS.TABS.METRICS'),
    icon: 'i-ph-chart-bar',
  },
  {
    id: 'sessions',
    label: t('AGENT_REPORTS.TABS.SESSIONS'),
    icon: 'i-ph-list-checks',
  },
]);

const switchTab = tabId => {
  activeTab.value = tabId;
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div v-if="agent.id" class="agent-report-page flex flex-col">
    <!-- Tab Navigation -->
    <div
      class="flex items-center gap-2 mt-4 mb-4 mx-6 border-b border-n-weak pb-3"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
        :class="
          activeTab === tab.id
            ? 'bg-woot-500 text-white'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
        "
        @click="switchTab(tab.id)"
      >
        <span :class="tab.icon" class="text-base" />
        {{ tab.label }}
      </button>
    </div>

    <!-- Metrics Tab (existing) -->
    <WootReports
      v-if="activeTab === 'metrics'"
      :key="agent.id"
      type="agent"
      getter-key="agents/getAgents"
      action-key="agents/get"
      :selected-item="agent"
      :download-button-label="$t('AGENT_REPORTS.DOWNLOAD_AGENT_REPORTS')"
      :report-title="$t('AGENT_REPORTS.HEADER')"
      has-back-button
    />

    <!-- Sessions Tab (new) -->
    <div v-if="activeTab === 'sessions'" class="px-6">
      <!-- Header for Sessions Tab -->
      <div class="mb-4">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('AGENT_REPORTS.SESSIONS.HEADER', { name: agent.name }) }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ $t('AGENT_REPORTS.SESSIONS.DESCRIPTION') }}
        </p>
      </div>

      <AgentSessionsTab :agent-id="agent.id" :inboxes="inboxes" />
    </div>
  </div>
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>

<style scoped>
.agent-report-page {
  background-color: var(--wlab-bg-primary);
  min-height: 100%;
}
</style>
